import type { UpdateLeadPayload } from '../../../types/leads';

export interface LeadUpdateFormValues {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  jobTitle: string;
  companyWebsiteUrl: string;
  companyLinkedinUrl: string;
  tags: string;
  assignedTo: string | null;
}

export interface LeadUpdateStoredValues {
  email: string;
  phone: string;
  company: string;
  jobTitle: string;
  customFields: Record<string, unknown> | null | undefined;
}

/**
 * Maps an editable text field to its PATCH value:
 * - unchanged (trimmed value equals stored)  -> undefined (omitted from the request)
 * - cleared (emptied a previously-set value) -> null (persisted as a clear)
 * - changed to a new value                   -> the trimmed value
 *
 * Sending `undefined` for a cleared field is what caused FLOSEAI-565: the key was
 * dropped from the JSON body, so the backend treated it as "no change" and kept the
 * old value. Only fields the user actually changed are sent, so editing an unrelated
 * field never re-submits (or re-validates) a field left untouched.
 */
function clearableFieldUpdate(current: string, stored: string): string | null | undefined {
  const trimmed = current.trim();
  if (trimmed === stored.trim()) return undefined;
  return trimmed || null;
}

function readCompanyProfile(
  customFields: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const profile = customFields?.companyProfile;
  return profile && typeof profile === 'object' ? (profile as Record<string, unknown>) : {};
}

function readUrl(profile: Record<string, unknown>, key: string): string {
  const value = profile[key];
  return typeof value === 'string' ? value : '';
}

export function buildLeadUpdatePayload(
  values: LeadUpdateFormValues,
  stored: LeadUpdateStoredValues,
): UpdateLeadPayload {
  const {
    fullName,
    email,
    phone,
    company,
    jobTitle,
    companyWebsiteUrl,
    companyLinkedinUrl,
    tags,
    assignedTo,
  } = values;

  const storedCompanyProfile = readCompanyProfile(stored.customFields);
  const companyUrlChanged =
    companyWebsiteUrl.trim() !== readUrl(storedCompanyProfile, 'companyUrl');
  const companyLinkedinChanged =
    companyLinkedinUrl.trim() !== readUrl(storedCompanyProfile, 'companyLinkedinUrl');

  const companyProfile: Record<string, unknown> = { ...storedCompanyProfile };
  if (companyWebsiteUrl.trim()) companyProfile.companyUrl = companyWebsiteUrl.trim();
  else delete companyProfile.companyUrl;
  if (companyLinkedinUrl.trim()) companyProfile.companyLinkedinUrl = companyLinkedinUrl.trim();
  else delete companyProfile.companyLinkedinUrl;

  return {
    fullName: fullName || undefined,
    email: clearableFieldUpdate(email, stored.email),
    phone: clearableFieldUpdate(phone, stored.phone),
    company: clearableFieldUpdate(company, stored.company),
    jobTitle: clearableFieldUpdate(jobTitle, stored.jobTitle),
    customFields: companyUrlChanged || companyLinkedinChanged ? { companyProfile } : undefined,
    tags: tags
      ? tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean)
      : [],
    assignedTo,
  };
}
